// Copyright (c) Docugami, Inc. All rights reserved.

import { skipToken } from '@reduxjs/toolkit/dist/query';
import { FolderId, createFolderId, createFolderIdString, isUnassigned } from 'components/file_management/file_helpers';
import produce from 'immer';
import React, { useContext, useMemo } from 'react';
import type { AppDispatch, AppState } from 'store';
import { store } from 'store';
import { createXhtml } from 'store/dgml/dgmlActions';
import { selectDgmlNodesByFolderId, selectIsDgmlInitialized, selectMappedTokens, selectSpansIndexedByLabel, selectTokens } from 'store/dgml/dgmlSelectors';
import { DgmlData, DgmlResourceRequest, chunkEntityAdapter, dgmlSlice, selectAllChunks, selectChunkBySpanId, useGetDgmlNodesQuery, useGetPagesInfoQuery, useGetTokensQuery } from 'store/dgml/dgmlSlice';
import { Chunk, ChunkStructure, chunkPostOrderTraversal, isChunkAddedChunk, isSpanIdAddedChunk, isTableStructure } from 'store/dgml/dgmlTypes';
import type { LabelChunkRequest, ResizeChunkRequest } from 'store/frontend/frontendSlice';
import { selectLabelState } from 'store/frontend/selectors';
import { IMappedTokens } from 'utils/MappedTokens';
import { SpanIdUtils } from 'utils/SpanIdUtils';
import { TokenRangeUtils } from 'utils/TokenRangeUtils';
import { TokensTextParser } from 'utils/TokensTextParser';
import { DgmlVersion, DgmlVersionSchema4 } from 'utils/dgmldom';
import { ChunkInstance, TokenRange } from '../networking/api';
import { useAppSelector } from './redux';

// Empty object constant to avoid creating new references in selectors
const EMPTY_LABEL_INDEXED_SPAN_IDS: Record<string, string[]> = {};

// Lazy-loaded to avoid circular dependency - will be populated on first use
let _useGetLabelsQuery: typeof import('store/frontend/frontendSlice').useGetLabelsQuery | null = null;
const getUseGetLabelsQuery = () => {
    if (!_useGetLabelsQuery) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        _useGetLabelsQuery = require('store/frontend/frontendSlice').useGetLabelsQuery;
    }
    return _useGetLabelsQuery!;
};

const DefaultContext: IDocumentDgml = {
    getInstancesFlat: () => [],
    getInstanceById: () => null,
    label: () => { throw new Error("Invalid DGML context") },
    removeLabel: () => { throw new Error("Invalid DGML context") },
    resize: () => { throw new Error("Invalid DGML context") },
    isInitialized: () => false,
    getHtmlStringForSpanId: () => { throw new Error("Invalid DGML context") },
    getTokensTextParser: () => { throw new Error("Invalid DGML context") },
    getRootSpanId: () => { throw new Error("Invalid DGML context") },
    getAxisIds: () => { throw new Error("Invalid DGML context") },
    getSpanIdsForLabel: () => [],
    getStructureForSpanId: () => { throw new Error("Invalid DGML context") },
    documentId: '',
    docsetId: '',
    fileId: ''
};

export const DocumentDgmlContext = React.createContext<IDocumentDgml>(DefaultContext);

export interface IDocumentDgml {
    getInstancesFlat(): ChunkInstance[];
    getInstanceById(id: string): ChunkInstance | null;
    label(request: Omit<LabelChunkRequest, "docsetId" | "fileId">): Promise<ChunkInstance>
    removeLabel(request: Omit<LabelChunkRequest, "docsetId" | "fileId">): Promise<ChunkInstance>
    resize(request: Omit<ResizeChunkRequest, "docsetId" | "fileId">): Promise<ChunkInstance>
    isInitialized(): boolean;
    getHtmlStringForSpanId(spanId: string | undefined): string | undefined;
    getTokensTextParser(): TokensTextParser;
    getRootSpanId(): string;
    getAxisIds(spanId: string): AxisIds;
    getSpanIdsForLabel(label: string): string[];
    getStructureForSpanId(spanId: string): string | undefined;
    version?: DgmlVersionSchema4;
    documentId: string;
    docsetId: string;
    fileId: string;
}

export enum ChunkAxis {
    Parent = 'Parent',
    FirstChild = 'FirstChild',
    PreviousSibling = 'PreviousSibling',
    NextSibling = 'NextSibling'
}

export type AxisIds = {
    [Key in keyof typeof ChunkAxis]?: string
}

export interface EditedChunk {
    instance: ChunkInstance;
    text: string;
}

interface DgmlState {
    folderId: FolderId,
    dgmlData: DgmlData,
    tokens: TokensTextParser,
    mappedTokens: IMappedTokens,
    selectChunk(spanId?: string): Chunk | undefined;
}

class DgmlInstances {
    private getDgmlState(folderId: string): DgmlState | undefined {
        const fid = createFolderId(folderId);
        const isInitialized = selectIsDgmlInitialized(store.getState(), fid.clusterId, fid.fileId);
        if (isInitialized) {
            const state = store.getState();
            const dgmlData = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(state)!.data!;
            return {
                folderId: fid,
                dgmlData: dgmlData,
                tokens: new TokensTextParser(selectTokens(state, fid.clusterId, fid.fileId)!),
                mappedTokens: selectMappedTokens(state, fid.clusterId, fid.fileId)!,
                selectChunk: spanId => selectChunkBySpanId(dgmlData.chunks, spanId ?? '')
            }
        }
    }

    public isInitialized(folderId: string): boolean {
        if (!folderId) {
            return false;
        }

        const fid = createFolderId(folderId);
        return selectIsDgmlInitialized(store.getState(), fid.clusterId, fid.fileId);
    }

    public getInstancesFlat(folderId: string): ChunkInstance[] {
        const state = this.getDgmlState(folderId);
        if (!state) {
            return [];
        }

        return selectAllChunks(state.dgmlData.chunks).map(x => convertChunkToInstance(folderId, x));
    }

    public getSpanIdForTokenRange(folderId: string, tokenRange: TokenRange, subtreeId?: string): string {
        const state = this.getDgmlState(folderId);
        if (!state) {
            throw new Error("Not initialized");
        }

        const tokenKeyRange = state.mappedTokens.tokenRangeToTokenKeyRange(tokenRange);
        if (!tokenKeyRange) {
            throw new Error("Could not convert token range.")
        }

        return SpanIdUtils.create(state.folderId.fileId, tokenKeyRange.fromTokenKey, tokenKeyRange.toTokenKey, subtreeId).value;
    }

    public isChunkInDgml(folderId: string, spanId: string): boolean {
        const state = this.getDgmlState(folderId);
        if (!state) {
            return false;
        }

        return !!state.dgmlData.chunks.entities[spanId];
    }

    public getInstanceById(folderId: string, id: string): ChunkInstance | null {
        if (!folderId || !id) {
            return null;
        }

        const state = this.getDgmlState(folderId);
        if (!state) {
            return null;
        }

        let chunk = state.dgmlData.chunks.entities[id];
        if (!chunk && !isSpanIdAddedChunk(id)) {
            const spanId = SpanIdUtils.tryParse(id);
            if (!!spanId) {
                const tokenKeyRange = { fromTokenKey: spanId.fromTokenKey, toTokenKey: spanId.toTokenKey };
                const tokenRange = state.mappedTokens.tokenKeyRangeToTokenRange(tokenKeyRange);
                if (!tokenRange) {
                    return null;
                }

                chunk = {
                    docsetId: state.folderId.clusterId,
                    fileId: state.folderId.fileId,
                    spanId: spanId.value,
                    tokenKeyRange: { fromTokenKey: spanId.fromTokenKey, toTokenKey: spanId.toTokenKey },
                    labels: [],
                    children: [],
                    tokenRange: tokenRange,
                    transient: true
                }
            }
        }

        return !!chunk ? convertChunkToInstance(folderId, chunk) : null;
    }

    public async label(request: LabelChunkRequest): Promise<ChunkInstance> {
        // Dynamic import to avoid circular dependency
        const { frontendSlice } = await import('store/frontend/frontendSlice');
        await store.dispatch(frontendSlice.endpoints.labelChunk.initiate(request));
        return this.getInstanceById(createFolderIdString(request.docsetId, request.fileId), request.spanId)!;
    }

    public async removeLabel(request: LabelChunkRequest): Promise<ChunkInstance> {
        // Dynamic import to avoid circular dependency
        const { frontendSlice } = await import('store/frontend/frontendSlice');
        await store.dispatch(frontendSlice.endpoints.removeLabel.initiate(request));
        return this.getInstanceById(createFolderIdString(request.docsetId, request.fileId), request.spanId)!;
    }

    public async resize(request: ResizeChunkRequest): Promise<ChunkInstance> {
        // Dynamic import to avoid circular dependency
        const { frontendSlice } = await import('store/frontend/frontendSlice');
        await store.dispatch(frontendSlice.endpoints.resize.initiate(request));
        const originalSpanId = SpanIdUtils.parse(request.spanId);
        const newSpanId = SpanIdUtils.create(request.fileId, request.tokenKeyRange.fromTokenKey, request.tokenKeyRange.toTokenKey, originalSpanId.subtreeId);
        return this.getInstanceById(createFolderIdString(request.docsetId, request.fileId), newSpanId.value)!;
    }

    public isStructuralChunk(instance: ChunkInstance): boolean {
        const state = this.getDgmlState(instance.folderId);
        if (!state) {
            return false;
        }

        if (!!instance.spanId) {
            const chunk = state.dgmlData.chunks.entities[instance.spanId];
            if (!!chunk) {
                return this.containsStructuralChunkNodeHelper(chunk, state);
            }
        }

        return false;
    }

    public isTableChunk(instance: ChunkInstance): boolean {
        const state = this.getDgmlState(instance.folderId);
        if (!state) {
            return false;
        }

        const chunk = state.dgmlData.chunks.entities[instance.spanId ?? ''];
        return isTableStructure(chunk?.structure?.type);
    }

    public getAxisIds(chunkId: string, folderId: string): AxisIds {
        const state = this.getDgmlState(folderId);
        if (!state) {
            return {};
        }

        const chunk = state.selectChunk(chunkId);
        if (!chunk || isChunkAddedChunk(chunk)) {
            return {};
        }

        const isValid = (chunk?: Chunk): boolean => !!chunk &&
            chunk.structure?.type !== ChunkStructure.TableBody &&
            !isChunkAddedChunk(chunk) &&
            chunk.structure?.type !== ChunkStructure.OutflowContainer;

        let parent = state.selectChunk(chunk.parent);
        if (parent?.structure?.type === ChunkStructure.OutflowContainer) {
            parent = undefined;
        }

        while (!!parent?.parent && !isValid(parent)) {
            parent = state.selectChunk(parent.parent);
        }

        let firstChild: string | undefined;
        for (const childId of chunk.children) {
            // Skip table bodies
            let chunk = state.selectChunk(childId);
            if (chunk?.structure?.type === ChunkStructure.TableBody) {
                chunk = state.selectChunk(chunk.children[0]);
            }

            if (isValid(chunk)) {
                firstChild = chunk!.spanId;
                break;
            }
        }

        let previousSibling;
        let nextSibling;
        const chunkParent = state.selectChunk(chunk.parent);
        if (!!chunkParent) {
            const currentIndex = chunkParent.children.indexOf(chunk.spanId);
            for (let i = currentIndex - 1; i >= 0; --i) {
                const siblingSpanId = chunkParent.children[i];
                if (isValid(state.selectChunk(siblingSpanId))) {
                    previousSibling = siblingSpanId;
                    break;
                }
            }

            for (let i = currentIndex + 1; i < chunkParent.children.length; ++i) {
                const siblingSpanId = chunkParent.children[i];
                if (isValid(state.selectChunk(siblingSpanId))) {
                    nextSibling = siblingSpanId;
                    break;
                }
            }
        }

        return {
            Parent: parent?.spanId,
            FirstChild: firstChild,
            PreviousSibling: previousSibling,
            NextSibling: nextSibling
        }
    }

    public getRootSpanId(folderId: string): string {
        const state = this.getDgmlState(folderId);
        return state?.dgmlData.root ?? '';
    }

    public getRootElement(folderId: string): Chunk | undefined {
        const state = this.getDgmlState(folderId);
        if (!!state) {
            const rootChunk = state.dgmlData.chunks.entities[state.dgmlData.root]!;
            return rootChunk;
        }
    }

    public getTokensTextParser(folderId: string): TokensTextParser {
        const state = this.getDgmlState(folderId);
        if (!state) {
            throw new Error("Not initialized");
        }

        return state.tokens;
    }

    public isStructuralChunkNode(chunk: Chunk): boolean {
        if (!chunk) {
            return false;
        }

        if (!chunk.parent) {
            return true;
        }

        if (!!chunk.structure) {
            return chunk.structure.type !== ChunkStructure.ListItemMarker;
        }

        return false;
    }

    public containsStructuralChunkNode(chunk: Chunk, folderId: string): boolean {
        const state = this.getDgmlState(folderId)!;
        if (!state) {
            return false;
        }

        return this.containsStructuralChunkNodeHelper(chunk, state);
    }

    private containsStructuralChunkNodeHelper(chunk: Chunk, state: DgmlState): boolean {
        const iter = chunkPostOrderTraversal(chunk, state.dgmlData.chunks.entities);
        let result = iter.next();
        while (!!result.value) {
            if (this.isStructuralChunkNode(result.value)) {
                return true;
            }

            result = iter.next();
        }

        return false;
    }

    public getEnclosingHyperline(token: number, folderId: string): ChunkInstance | null {
        const state = this.getDgmlState(folderId);
        if (!state) {
            return null;
        }

        const root = state.selectChunk(state.dgmlData.root)!;
        const iter = chunkPostOrderTraversal(root, state.dgmlData.chunks.entities);
        let result = iter.next();
        while (!!result.value) {
            const current = result.value as Chunk;
            if (this.isStructuralChunkNode(current) && current.tokenRange.fromTok <= token && current.tokenRange.toTok > token) {
                return convertChunkToInstance(folderId, current);
            }
        }

        return null;
    }

    public isNonResizableChunk(chunk: ChunkInstance): boolean {
        if (!chunk.spanId) {
            return false;
        }

        const state = this.getDgmlState(chunk.folderId);
        const chunkNode = state?.selectChunk(chunk.spanId);
        if (!chunkNode) {
            return false;
        }

        return !chunkNode.parent || !!chunkNode.structure;
    }

    public getDgmlVersion(folderId: string): DgmlVersion | null {
        const state = this.getDgmlState(folderId);
        return state?.dgmlData.version ?? null;
    }

    public getHtmlStringBySpanId(spanId: string, folderId: string): string | undefined {
        return store.dispatch(getHtmlStringForChunk(folderId, spanId))
    }

    public getMappedTokens(folderId: string): IMappedTokens | undefined {
        const state = this.getDgmlState(folderId);
        if (!state) {
            return;
        }

        return state.mappedTokens;
    }
}

const convertChunkToInstance = (folderId: string, chunk: Chunk): ChunkInstance => {
    const isAddedChunk: boolean = isChunkAddedChunk(chunk);

    let labelId: string | undefined;
    let isTransform = false;
    const state = store.getState();
    const labelState = selectLabelState(chunk.docsetId)(state).data;
    if (!!labelState && chunk.labels?.length > 0) {
        const chunkLabel = chunk.labels[0];
        // Inline selector to avoid circular dependency with frontendSlice
        const allLabels = Object.values(labelState.entities).filter(Boolean);
        labelId = allLabels.find(x => x?.label === chunkLabel.value)?.id;
        isTransform = isAddedChunk && !!labelId && chunkLabel.toolId === "dg-transform";
    }

    const chunkInstance: ChunkInstance = {
        chunkInstanceId: chunk.spanId,
        spanId: chunk.spanId,
        folderId: folderId,
        chunk: chunk,
        tokenRange: chunk.tokenRange,
        labelId: labelId,
        isAddedChunk: isAddedChunk,
        isTransform: isTransform 
    }

    return chunkInstance;
}

export const DgmlInstancesSingleton = new DgmlInstances();

export function useDocumentDgml(documentId?: string): IDocumentDgml {
    const context = useContext(DocumentDgmlContext);
    return documentId !== '' ? context : DefaultContext;
}

export function useDocumentDgmlContext(folderId: string): IDocumentDgml {
    const hasCpml = useAppSelector(state => !!state.documents.indexed[folderId]?.clusterDocument?.hasCpml);
    const dgmlVersion = useAppSelector(state => selectDgmlNodesByFolderId(state, folderId)?.version);

    const folderIdObject = useMemo(() => !!folderId ? createFolderId(folderId) : undefined, [folderId]);
    const isDgmlSupported = !!folderIdObject && !isUnassigned(folderId) && !!hasCpml;
    const dgmlResourceRequest: DgmlResourceRequest | undefined = useMemo(() => {
        if (isDgmlSupported && !!folderIdObject) {
            return {
                docsetId: folderIdObject.clusterId,
                fileId: folderIdObject.fileId
            }
        }
    }, [folderIdObject, isDgmlSupported]);

    const isDgmlInitialized = useAppSelector(state => !!dgmlResourceRequest ? selectIsDgmlInitialized(state, dgmlResourceRequest.docsetId, dgmlResourceRequest.fileId) : false)

    const labelIndexedSpanIds = useAppSelector(state => !!dgmlResourceRequest ? selectSpansIndexedByLabel(state, dgmlResourceRequest.docsetId, dgmlResourceRequest.fileId) : EMPTY_LABEL_INDEXED_SPAN_IDS);

    const isDgmlQueryEnabled = isDgmlSupported;
    const { currentData: dgmlResponse } = useGetDgmlNodesQuery(isDgmlQueryEnabled ? dgmlResourceRequest! : skipToken);

    const textResourceRequest: DgmlResourceRequest | undefined = useMemo(() => {
        if (!!folderIdObject) {
            if (isDgmlSupported && !!dgmlVersion?.textEventStreamVersion) {
                return {
                    docsetId: folderIdObject.clusterId,
                    fileId: folderIdObject.fileId,
                    version: dgmlVersion.textEventStreamVersion.toString()
                }
            }
            else if (!isDgmlSupported) {
                return {
                    docsetId: folderIdObject.clusterId,
                    fileId: folderIdObject.fileId,
                    version: '0'
                }
            }
        }
    }, [folderIdObject, dgmlVersion?.textEventStreamVersion, isDgmlSupported])

    useGetTokensQuery(textResourceRequest ?? skipToken);
    useGetPagesInfoQuery(textResourceRequest ?? skipToken);
    // Use lazy-loaded hook to avoid circular dependency
    getUseGetLabelsQuery()(!!dgmlResourceRequest ? dgmlResourceRequest.docsetId : skipToken);

    function getInstancesFlat(): ChunkInstance[] {
        if (!isDgmlInitialized) {
            return [];
        }

        return DgmlInstancesSingleton.getInstancesFlat(folderId);
    }

    function getInstanceById(id: string): ChunkInstance | null {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return DgmlInstancesSingleton.getInstanceById(folderId, id);
    }

    function label(request: Omit<LabelChunkRequest, "docsetId" | "fileId">): Promise<ChunkInstance> {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return DgmlInstancesSingleton.label({
            docsetId: folderIdObject!.clusterId,
            fileId: folderIdObject!.fileId,
            ...request
        });
    }

    function removeLabel(request: Omit<LabelChunkRequest, "docsetId" | "fileId">): Promise<ChunkInstance> {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return DgmlInstancesSingleton.removeLabel({
            docsetId: folderIdObject!.clusterId,
            fileId: folderIdObject!.fileId,
            ...request
        });
    }

    function resize(request: Omit<ResizeChunkRequest, "docsetId" | "fileId">): Promise<ChunkInstance> {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return DgmlInstancesSingleton.resize({
            docsetId: folderIdObject!.clusterId,
            fileId: folderIdObject!.fileId,
            ...request
        });
    }

    function getTokensTextParser(): TokensTextParser {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return DgmlInstancesSingleton.getTokensTextParser(folderId);
    }

    function getRootSpanId(): string {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return dgmlResponse!.root;
    }

    function getAxisIds(id: string): AxisIds {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return DgmlInstancesSingleton.getAxisIds(id, folderId);
    }

    function getHtmlStringForSpanId(spanId: string): string | undefined {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return DgmlInstancesSingleton.getHtmlStringBySpanId(spanId, folderId);
    }

    function getSpanIdsForLabel(label: string) {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return labelIndexedSpanIds[label] ?? [];
    }

    function getStructureForSpanId(spanId: string): string | undefined {
        if (!isDgmlInitialized) {
            throw new Error("DGML not initialized yet.");
        }

        return selectChunkBySpanId(dgmlResponse!.chunks, spanId)?.structure?.type;
    }

    return useMemo<IDocumentDgml>(() => {
        return {
            getInstancesFlat: getInstancesFlat,
            getInstanceById: getInstanceById,
            label: label,
            removeLabel: removeLabel,
            resize: resize,
            isInitialized: () => isDgmlInitialized,
            getTokensTextParser: getTokensTextParser,
            getRootSpanId: getRootSpanId,
            getHtmlStringForSpanId: getHtmlStringForSpanId,
            getAxisIds: getAxisIds,
            getSpanIdsForLabel: getSpanIdsForLabel,
            getStructureForSpanId: getStructureForSpanId,
            documentId: folderId,
            version: dgmlResponse?.version,
            docsetId: dgmlResourceRequest?.docsetId ?? '',
            fileId: dgmlResourceRequest?.fileId ?? ''
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dgmlResponse, isDgmlInitialized, folderId]);
}

export const useTextVersion = (documentId: string): string | undefined => {
    const hasCpml = useAppSelector(state => state.documents.indexed[documentId]?.clusterDocument?.hasCpml);
    const { version } = useDocumentDgml(documentId);

    return hasCpml !== undefined ? (hasCpml ? version?.textEventStreamVersion.toString() : '0') : undefined;
}

const getHtmlStringForChunk = (folderId: string, spanId: string, ignoreText?: boolean) => (dispatch: AppDispatch, getState: () => AppState): string | undefined => {
    const fid = createFolderId(folderId);

    let dgmlState = dgmlSlice.endpoints.getDgmlNodes.select({ docsetId: fid.clusterId, fileId: fid.fileId })(getState())?.data;
    if (!dgmlState) {
        return;
    }

    let chunks = dgmlState.chunks;
    let chunk = chunks.entities[spanId];
    if (!chunk) {
        // This chunk doesn't exist, but we can create an ephemeral chunk node and generate HTML from that
        const ephemeralChunk = DgmlInstancesSingleton.getInstanceById(folderId, spanId);
        if (!ephemeralChunk?.chunk) {
            return;
        }

        chunk = ephemeralChunk.chunk;

        // 1. Get the chunk that contains the target token range
        let parentChunk = chunks.entities[dgmlState.root]!;
        let shouldContinue = true;
        while (shouldContinue) {
            shouldContinue = false;
            for (const childId of parentChunk.children) {
                const child = chunks.entities[childId]!;
                if (TokenRangeUtils.rangeIsContained(chunk.tokenRange, child.tokenRange)) {
                    parentChunk = child;
                    shouldContinue = true;
                }
            }
        }

        // 2. Update a copy of our chunk lookup as if this ephemeral chunk existed. For simplicity, we just make all children of the ephemeral node's
        //    parent new children of the ephemeral node. We know any node not contained by the root will get dropped when creating the HTML
        chunk.children = [...parentChunk.children];
        chunks = produce(chunks, x => {
            chunkEntityAdapter.upsertMany(x, parentChunk.children.map(childId => ({
                ...(x.entities[childId]!),
                parent: chunk!.spanId
            })))
        });
    }

    const tokens = !ignoreText ? selectTokens(getState(), fid.clusterId, fid.fileId) : undefined;

    const dom = createXhtml(chunk, chunks.entities, tokens);
    return dom?.toString();
}
